import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, FileText, Loader2 } from "lucide-react";
import { useWorkspace } from "./WorkspaceContext";
import {
  sessionPath,
  sessionRoute,
  sessionReturnPath,
  withSessionReturn,
  sourceSegmentIndex,
  type SessionStep,
} from "./sessionRoutes";
import { workspaceCopy } from "./copy";
import { SessionTrail } from "./SessionTrail";
import {
  conversationFlowCopy,
  hasSessionTranscript,
  nextSessionStep,
} from "./sessionFlow";
import { registerNavigationGuard } from "./navigationGuards";
import { useClients } from "@/shared/hooks/useClients";
import { useClientSessions } from "@/shared/hooks/useClientSessions";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { go } from "@/shared/dashboard/shell/navigate";
import { RecordingPanel } from "@/shared/components/recording/RecordingPanel";
import { TranscriptImportPanel } from "@/shared/components/recording/TranscriptImportPanel";
import { TranscriptList } from "@/shared/components/recording/TranscriptList";
import { MarkdownEditor } from "@/shared/components/recording/MarkdownEditor";
import { ConversationSummary } from "./ConversationSummary";
import { AISuggestionsPanel } from "@/shared/components/recording/AISuggestionsPanel";
import { TagChips } from "@/shared/components/recording/TagChips";
import { createAutosaver } from "@/shared/components/recording/autosave";
import { toolFlowCopy } from "./toolFlowCopy";
import { RecordingStandardPanel } from "./RecordingStandardPanel";
import { conversationAnalysisCopy } from "./conversationAnalysisCopy";
import {
  analyzeConversation,
  transcribeConversation,
} from "@/shared/components/recording/conversationProcessing";
import { supabase } from "@/shared/lib/supabase";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";

const AudioPlayerBar = lazy(() =>
  import("@/shared/components/recording/AudioPlayerBar").then((module) => ({
    default: module.AudioPlayerBar,
  })),
);

export function ConnectedSessionPage() {
  const workspace = useWorkspace();
  // Outside the general shell (e.g. a course-locked cohort) there is no
  // workspace context; the URL itself still names the recording.
  const route = sessionRoute(workspace?.pathname ?? window.location.pathname);
  return (
    route && (
      <ConnectedSession
        key={route.sessionId}
        clientId={route.clientId}
        sessionId={route.sessionId}
        requestedStep={route.step}
      />
    )
  );
}

function ConnectedSession({
  clientId,
  sessionId,
  requestedStep,
}: {
  clientId: string;
  sessionId: string;
  requestedStep: SessionStep | null;
}) {
  const { locale } = useLanguage();
  const copy = workspaceCopy(locale);
  const flow = conversationFlowCopy(locale);
  const analysisCopy = conversationAnalysisCopy(locale);
  const { hasAccess, requestUpgrade } = useSubscription();
  const actions = toolFlowCopy(locale);
  const returnTo = sessionReturnPath(window.location.search);
  const sourceParams = new URLSearchParams(window.location.search);
  const preferTranscript = sourceParams.get("transcript") === "1";
  const preferUpload = sourceParams.get("upload") === "1";
  const autoAnalyze = sourceParams.get("analyze") === "1";
  const pathFor = (next: SessionStep) =>
    withSessionReturn(sessionPath(clientId, sessionId, next), returnTo);
  const {
    clients,
    loading: clientsLoading,
    error: clientsError,
  } = useClients();
  const { sessions, loading, error, refresh, updateSession } =
    useClientSessions(clientId);
  const client = clients.find((item) => item.id === clientId);
  const session = sessions.find((item) => item.id === sessionId);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<
    "transcribing" | "analyzing" | null
  >(null);
  const [analysisError, setAnalysisError] = useState(false);
  const [analysisRetry, setAnalysisRetry] = useState(0);
  const attemptedAnalysis = useRef(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [highlightedSegmentIndex, setHighlightedSegmentIndex] = useState<
    number | null
  >(null);
  const transcriptContainer = useRef<HTMLDivElement>(null);
  const playhead = useRef(0);
  const latestNotes = useRef(notes);
  const updateRef = useRef(updateSession);
  updateRef.current = updateSession;
  latestNotes.current = notes;
  const dirtyRef = useRef(false);
  const initialized = useRef(false);
  const navigationGuard = useRef<() => Promise<boolean>>(async () => true);
  useEffect(() => registerNavigationGuard(() => navigationGuard.current()), []);
  const step = nextSessionStep(session, requestedStep);
  const stepRef = useRef(step);
  stepRef.current = step;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      !session ||
      !hasAccess ||
      attemptedAnalysis.current ||
      requestedStep === "note"
    )
      return;
    const hasSource = hasSessionTranscript(session);
    if (
      session.summary?.trim() ||
      (!hasSource && !(autoAnalyze && session.audio_path))
    )
      return;
    if (hasSource && stepRef.current !== "analysis") return;
    attemptedAnalysis.current = true;
    let active = true;
    setAnalysisError(false);
    setProcessing(hasSource ? "analyzing" : "transcribing");
    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error("No authenticated session");
        if (!hasSource) await transcribeConversation(sessionId, token);
        if (active) setProcessing("analyzing");
        await analyzeConversation(sessionId, token, locale);
        if (mounted.current) await refresh();
        if (active && mounted.current && stepRef.current === "recording")
          go(pathFor("analysis"));
      } catch (error) {
        console.error("Conversation analysis failed:", error);
        attemptedAnalysis.current = false;
        if (active) {
          setAnalysisError(true);
          await refresh();
        }
      } finally {
        if (mounted.current) setProcessing(null);
      }
    })();
    return () => {
      active = false;
    };
    // Source data is captured once per attempt. Refreshing a session must not
    // cancel its pipeline or start another generation while it is in progress.
  }, [session?.id, requestedStep, hasAccess, analysisRetry]);

  const saver = useMemo(
    () =>
      createAutosaver(async (value) => {
        setSaving(true);
        setSaveError(null);
        try {
          await updateRef.current(sessionId, { notes: value || null });
          if (value === latestNotes.current) {
            dirtyRef.current = false;
            setDirty(false);
          }
        } catch (error) {
          setSaveError(workspaceCopy(locale).saveError);
          throw error;
        } finally {
          setSaving(false);
        }
      }, 1000),
    [sessionId, locale],
  );

  useEffect(() => {
    if (!session || dirtyRef.current || saver.hasPending()) return;
    if (!initialized.current || session.notes !== latestNotes.current)
      setNotes(session.notes ?? "");
    initialized.current = true;
  }, [session?.notes, session?.id, saver]);
  useEffect(
    () => () => {
      void saver.flush().catch(() => {});
    },
    [saver],
  );
  useEffect(() => {
    const preventLoss = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !saver.hasPending()) return;
      void saver.flush().catch(() => {});
      event.preventDefault();
    };
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, [saver]);
  useEffect(() => {
    if (!session || requestedStep) return;
    const query = window.location.search;
    go(`${sessionPath(clientId, sessionId, step)}${query}`);
  }, [session?.id, requestedStep, step, clientId, sessionId]);
  useEffect(() => {
    if (!session) return;
    const waitingForAudio = session.consent_confirmed_at && !session.audio_path;
    const interval = window.setInterval(
      () => {
        if (document.visibilityState === "visible") void refresh();
      },
      waitingForAudio ? 5000 : 30000,
    );
    return () => window.clearInterval(interval);
  }, [
    session?.consent_confirmed_at,
    session?.audio_path,
    session?.id,
    refresh,
  ]);
  useEffect(() => {
    if (transcriptOpen && highlightedSegmentIndex !== null) {
      transcriptContainer.current
        ?.querySelector(`[data-segment-index="${highlightedSegmentIndex}"]`)
        ?.scrollIntoView({ block: "center", behavior: "instant" });
    }
  }, [transcriptOpen, highlightedSegmentIndex]);

  const flush = async () => {
    try {
      // Reschedule after a failed autosave; its rejected value has been drained.
      if (dirtyRef.current) saver.schedule(latestNotes.current);
      await saver.flush();
      return true;
    } catch {
      return false;
    }
  };
  navigationGuard.current = flush;
  const changeStep = async (next: SessionStep) => {
    if (leaving) return;
    setLeaving(true);
    try {
      if (!(await flush())) return;
      go(pathFor(next));
    } finally {
      setLeaving(false);
    }
  };
  const finishConversation = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      if (await flush()) go(returnTo);
    } finally {
      setLeaving(false);
    }
  };
  const saveGeneratedNote = async (value: string) => {
    setNotes(value);
    latestNotes.current = value;
    dirtyRef.current = true;
    setDirty(true);
    saver.schedule(value);
    await saver.flush();
  };

  if ((!session && loading) || clientsLoading)
    return (
      <div className="eq-session-canvas" role="status">
        Loading session…
      </div>
    );
  if (error || clientsError || !session || !client)
    return (
      <div className="eq-session-canvas">
        <p role="alert">
          {error || clientsError ? copy.sessionError : copy.missingSession}
        </p>
        <button className="eq-button" onClick={() => void refresh()}>
          {copy.retry}
        </button>
      </div>
    );
  const hasTranscript = hasSessionTranscript(session);
  const suggestions = (
    <AISuggestionsPanel
      sessionId={sessionId}
      locale={locale}
      notesAreEmpty={!notes.trim()}
      hasExistingSummary={!!session.summary}
      onNoteGenerated={async (value) => {
        await saveGeneratedNote(value);
        go(pathFor("note"));
      }}
      onSummaryAppended={(value) =>
        saveGeneratedNote(`${latestNotes.current}\n\n${value}`.trim())
      }
      onNextStepsAppended={(value) =>
        saveGeneratedNote(`${latestNotes.current}\n\n${value}`.trim())
      }
      onSessionSummaryGenerated={refresh}
      showError={setSaveError}
      clinicalTemplates={false}
    />
  );
  const moments = [
    ...(session.key_moments ?? []),
    ...(session.time_markers ?? []),
  ].sort((a, b) => a.time_seconds - b.time_seconds);

  return (
    <div className="eq-session-page">
      <SessionTrail
        simple
        name={client.name}
        session={session}
        step={step}
        onStep={(next) => {
          if (next !== "record") void changeStep(next);
        }}
      />
      <div className="eq-session-canvas">
        <div className="eq-session-backbar">
          <button
            type="button"
            className="eq-button eq-button--quiet"
            disabled={leaving}
            onClick={async () => {
              if (await flush()) go(returnTo);
            }}
          >
            <ArrowLeft size={15} aria-hidden="true" />
            {actions.backConversations}
          </button>
        </div>
        {saveError && (
          <p className="eq-session-error" role="alert">
            {saveError}{" "}
            <button className="eq-button" onClick={() => void flush()}>
              {copy.retry}
            </button>
          </p>
        )}
        {(processing || analysisError) && (
          <section
            className="eq-session-surface eq-conversation-processing"
            aria-live="polite"
            aria-busy={!!processing}
          >
            {processing ? (
              <div role="status">
                <Loader2 size={22} className="eq-spin" aria-hidden="true" />
                <h2>{analysisCopy[processing]}</h2>
                <p>{analysisCopy.processingHint}</p>
              </div>
            ) : (
              <div role="alert">
                <p>{analysisCopy.failed}</p>
                <button
                  type="button"
                  className="eq-button eq-button--primary"
                  onClick={() => {
                    attemptedAnalysis.current = false;
                    setAnalysisRetry((value) => value + 1);
                  }}
                >
                  {analysisCopy.retry}
                </button>
              </div>
            )}
          </section>
        )}
        {session.audio_path && !processing && (
          <Suspense fallback={<p>Loading audio…</p>}>
            <AudioPlayerBar
              sessionId={sessionId}
              onTimeUpdate={(time) => {
                playhead.current = time;
              }}
            />
          </Suspense>
        )}
        {/* Keep source forms mounted between steps so in-progress text is not discarded. */}
        <div
          hidden={
            step !== "recording" ||
            !!processing ||
            (autoAnalyze && analysisError)
          }
        >
          {requestedStep === "analysis" && !hasTranscript && (
            <p className="eq-session-next" role="status">
              {flow.sourceFirst}
            </p>
          )}
          <div className="eq-page-heading eq-session-heading">
            <div>
              <h1>{copy.recording}</h1>
              <p>{copy.recordingHint}</p>
            </div>
          </div>
          <section className="eq-session-surface eq-session-capture-surface">
            <div className="eq-source-options">
              <TranscriptImportPanel
                locale={locale}
                transcript={session.transcript}
                authorized={
                  session.processing_authorization?.scope === "transcript" ||
                  session.processing_authorization?.scope === "audio"
                }
                autoFocus={preferTranscript}
                onSave={async (transcript) => {
                  await updateSession(sessionId, {
                    transcript,
                    transcript_segments: null,
                    summary: null,
                    key_moments: [],
                    status: "transcribed",
                    processingAuthorizationScope: "transcript",
                  });
                  attemptedAnalysis.current = false;
                  if (mounted.current) go(pathFor("analysis"));
                }}
              />
              <RecordingPanel
                session={session}
                locale={locale}
                preferUpload={preferUpload}
                autoTranscribe
                safeGeneral
                onAuthorizationConfirmed={async () => {
                  await updateSession(sessionId, {
                    processingAuthorizationScope: "audio",
                  });
                }}
                onRetentionPolicyChange={async (audioRetentionPolicy) => {
                  await updateSession(sessionId, { audioRetentionPolicy });
                }}
                onConsentConfirmed={async (iso) => {
                  await updateSession(sessionId, { consentConfirmedAt: iso });
                }}
                onAudioUploaded={() => {
                  void refresh();
                }}
                onAudioDeleted={() => {
                  void refresh();
                }}
                onTranscribed={async () => {
                  await refresh();
                  if (mounted.current && stepRef.current === "recording")
                    go(pathFor("analysis"));
                }}
              />
            </div>
          </section>
          <div className="eq-session-actions eq-session-next-action">
            <span>{!hasTranscript && copy.recordingFirst}</span>
            <button
              className="eq-button eq-button--primary"
              disabled={!hasTranscript}
              onClick={() => void changeStep("analysis")}
            >
              {flow.review}
              <ArrowRight size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
        {step === "analysis" && (
          <>
            <div className="eq-page-heading">
              <div>
                <h1>{analysisCopy.analysis}</h1>
                <p>{analysisCopy.analysisHint}</p>
              </div>
              <button
                className="eq-button eq-button--primary"
                onClick={() => void changeStep("note")}
                disabled={!hasTranscript}
              >
                <FileText size={15} />
                {copy.writeNote}
              </button>
            </div>
            {session.summary ? (
              <ConversationSummary
                summary={session.summary}
                sessionId={sessionId}
                locale={locale}
              />
            ) : !processing && !analysisError && !hasAccess ? (
              <button
                type="button"
                className="eq-button eq-button--primary"
                onClick={() =>
                  requestUpgrade({
                    action: "recording.analyze",
                    returnPath: `${window.location.pathname}${window.location.search}`,
                  })
                }
              >
                {flow.prepareReview}
              </button>
            ) : null}
            {moments.length > 0 && (
              <section className="eq-session-surface">
                <h2>{analysisCopy.moments}</h2>
                <ul className="eq-session-sources">
                  {moments.map((moment, index) => {
                    const sourceIndex = sourceSegmentIndex(
                      session.transcript_segments ?? [],
                      moment.time_seconds,
                    );
                    const source =
                      sourceIndex === null
                        ? null
                        : session.transcript_segments?.[sourceIndex];
                    return (
                      <li key={`${moment.time_seconds}-${index}`}>
                        <button
                          onClick={() => {
                            setHighlightedSegmentIndex(sourceIndex);
                            setTranscriptOpen(true);
                          }}
                          aria-label={`${copy.source}: ${moment.label}`}
                        >
                          <span>
                            {Math.floor(moment.time_seconds / 60)}:
                            {String(
                              Math.floor(moment.time_seconds % 60),
                            ).padStart(2, "0")}
                          </span>
                          <span>
                            {moment.label}
                            {source && <blockquote>“{source.text}”</blockquote>}
                          </span>
                          <ArrowRight size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
            {hasTranscript && (
              <details className="eq-conversation-optional">
                <summary>{analysisCopy.optional}</summary>
                <RecordingStandardPanel
                  sessionId={sessionId}
                  segments={session.transcript_segments ?? []}
                  locale={locale}
                />
                {suggestions}
              </details>
            )}
          </>
        )}
        {step === "note" && (
          <>
            <div className="eq-page-heading">
              <div>
                <h1>{copy.note}</h1>
                <p>{copy.notesHint}</p>
              </div>
              <button
                className="eq-button eq-button--primary"
                disabled={leaving}
                aria-busy={saving || leaving}
                onClick={() => void finishConversation()}
              >
                <Check size={15} />
                {leaving ? copy.saving : copy.done}
              </button>
            </div>
            <section className="eq-session-surface">
              <div
                onBlur={() => {
                  void flush();
                }}
              >
                <MarkdownEditor
                  value={notes}
                  prominent
                  ariaLabel={copy.note}
                  onChange={(value) => {
                    setNotes(value);
                    latestNotes.current = value;
                    dirtyRef.current = true;
                    setDirty(true);
                    saver.schedule(value);
                  }}
                />
              </div>
              <div className="eq-session-actions">
                <TagChips
                  tags={session.tags ?? []}
                  locale={locale}
                  onChange={(tags) => updateSession(sessionId, { tags })}
                />
                <span role="status">
                  {saving ? copy.saving : dirty ? copy.unsaved : copy.saved}
                </span>
              </div>
            </section>
            {hasTranscript && (
              <details>
                <summary>{copy.aiSuggestions}</summary>
                <div>{suggestions}</div>
              </details>
            )}
          </>
        )}
        {(hasTranscript || step !== "recording") && (
          <details
            open={transcriptOpen}
            onToggle={(event) => setTranscriptOpen(event.currentTarget.open)}
          >
            <summary>{copy.transcript}</summary>
            <div ref={transcriptContainer}>
              <TranscriptList
                session={session}
                clientName={client.name}
                locale={locale}
                neutralRoles
                highlightedSegmentIndex={highlightedSegmentIndex}
                getPlayheadTime={() => playhead.current}
                onSegmentsChange={(segments) =>
                  updateSession(sessionId, { transcript_segments: segments })
                }
                onAddTimeMarker={(marker) =>
                  updateSession(sessionId, {
                    time_markers: [...(session.time_markers ?? []), marker],
                  })
                }
              />
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
